/**
 * ArRowAnnotation
 *
 * Renders the annotation strip below an ArRow or a bundle header row — the only
 * two consumers (ArRow.tsx, ArBundleHeaderRow.tsx) — with inline editing,
 * blur-to-save and a Popconfirm delete control.
 * On screen the textarea grows to ten rows and caps fresh input at
 * MAX_LENGTHS.shortText; the wrapper carries no height clamp, or that autoSize
 * would never take effect, so the sticky delete gutter stretches to keep
 * occluding the full strip while the table scrolls sideways (SYD-2303).
 * PDF export has no scroll affordance, so the annotation prints as flowing text
 * at full length: workflow-written and pre-cap annotations exceed the input cap.
 * That text keeps `pre-wrap`, or the paragraph breaks the author typed collapse
 * into one run on the printed page while the textarea still shows them.
 * The strip sits under the table body's paste and context-menu handlers yet
 * outside every cell, so a gesture landing here releases
 * `cellFocus.focusedCell` — else a multi-line paste fills the last focused
 * cell's column and the row menu's Paste Cell writes there (SYD-2955).
 */

import { FC, memo, useState, useEffect, useContext } from 'react';
import { Button, Popconfirm, Input, Typography, theme } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import Icon from '../../atoms/Icon';
import FlexBox from '../../atoms/FlexBox';
import { usePdfExportMode } from '../usePdfExportMode';
import { ArTableActionsContext, ArTableUIStateContext } from '../context';
import { MAX_LENGTHS, type ArRowId } from '#types';

const { TextArea } = Input;
const { Text } = Typography;

type ArRowAnnotationProps = {
  rowId: ArRowId;
  annotation: string;
  isLocked: boolean;
  disabled?: boolean;
  rowBackground: string;
};

/**
 * ArRowAnnotation renders an editable annotation with delete control.
 */
const ArRowAnnotation: FC<ArRowAnnotationProps> = ({
  rowId,
  annotation,
  isLocked,
  disabled = false,
  rowBackground,
}) => {
  const { token } = theme.useToken();
  const actions = useContext(ArTableActionsContext)!;
  const uiState = useContext(ArTableUIStateContext)!;
  const isPdfExportMode = usePdfExportMode();

  /**
   * Focus or a right-click reaching this strip means the grid holds neither, so
   * the cell the user left stops being a target for the table's clipboard.
   */
  const releaseCellFocus = () => {
    if (uiState.cellFocus.focusedCell !== null) uiState.cellFocus.focusedCell = null;
  };

  const isReadOnly = disabled || isLocked;
  const backgroundColor = rowBackground;

  const [value, setValue] = useState(annotation);

  useEffect(() => {
    setValue(annotation);
  }, [annotation]);

  const handleBlur = () => {
    const trimmed = value.trim();
    if (trimmed !== annotation) {
      actions.updateAnnotation(rowId, trimmed || undefined);
    }
  };

  const handleDelete = () => {
    actions.updateAnnotation(rowId, undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setValue(annotation);
    }
  };

  return (
    <FlexBox
      alignStart
      aria-label={`artable-row-annotation-${rowId}`}
      onFocusCapture={releaseCellFocus}
      onContextMenuCapture={releaseCellFocus}
      style={{
        gridColumn: '1 / -1',
        backgroundColor,
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        paddingLeft: 20,
      }}
    >
      <Icon.CornerDownRight
        size={14}
        style={{
          color: token.colorTextSecondary,
          flexShrink: 0,
          marginTop: 4,
        }}
      />
      {isPdfExportMode ? (
        <Text
          data-testid={`artable-row-annotation-text-${rowId}`}
          style={{
            flex: 1,
            minWidth: 0,
            padding: `${token.paddingXXS}px ${token.paddingXS}px`,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          {annotation}
        </Text>
      ) : (
        <TextArea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Enter annotation..."
          autoSize={{ minRows: 1, maxRows: 10 }}
          maxLength={MAX_LENGTHS.shortText}
          aria-label="Row annotation input"
          disabled={isReadOnly}
          variant='borderless'
        />
      )}
      {!isPdfExportMode && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'stretch',
            width: 40,
            minWidth: 40,
            maxWidth: 40,
            position: 'sticky',
            right: 0,
            zIndex: 20,
            backgroundColor,
          }}
        >
          {!disabled && !isLocked && (
            <Popconfirm
              title="Delete annotation?"
              description="This will remove the annotation from this row."
              onConfirm={handleDelete}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
            >
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                aria-label="Delete row annotation"
              />
            </Popconfirm>
          )}
        </div>
      )}
    </FlexBox>
  );
};

export default memo(ArRowAnnotation);
